import { FORTNITE_LEADERBOARD_TYPES } from '../../../../mixins/games.commons';
import { pointsToRank } from '../../../../mixins/ranks.commons';
import * as ProfileHelpers from './ProfileHelpers';
import dayjs from '../../../../utils/dayjs';

export const FORTNITE_STATS_NAME = {
  avgKills: 'AVG kills',
  avgPlacement: 'AVG placement',
  wins: 'Total wins',
  kills: 'Kills',

  // Leaderboard
  [FORTNITE_LEADERBOARD_TYPES.POINTS]: 'Points',
  [FORTNITE_LEADERBOARD_TYPES.AVG_POSITIONS]: 'Avg placement',
  [FORTNITE_LEADERBOARD_TYPES.PLAYERS_OUTLIVED]: 'Players outlived',
  [FORTNITE_LEADERBOARD_TYPES.TOP_10_STREAK]: 'Top 10 steak',
};

export const FORTNITE_STATS_NAME_ABBR = {
};

export const FORTNITE_QUEUES_STATS_ORDER = [
  'avgKills',
  'avgPlacement',
  'wins',
];

export const FORTNITE_CORE_STATS_ORDER = [
  'avgKills',
  'avgPlacement',
];

export const FORTNITE_MONTHLY_STATS_TOOLTIP_ORDER = [
  'kills',
  'avgPlacement',
];

export const RANKS_TO_SHOW = [
  'hub',
  'state',
  'country',
];

export const FORTNITE_STATS_TYPES = {
};

export const FORTNITE_STATS_INVERTED = {
  avgPlacement: true,
};

export function getFeaturedStatisticsData(profileGameStatistics, profileGeneralDatal) {
  let rankAvatar;
  if (pointsToRank(profileGameStatistics.general.points)) {
    rankAvatar = 'boomtv/' + pointsToRank(profileGameStatistics.general.points);
  } else {
    rankAvatar = 'unknown';
  }

  return {
    avatar: profileGeneralDatal.avatar,
    rankAvatar, // Using shared BoomTV Ranks
    name: profileGameStatistics.general.playerName || 'unknown',
    rank: {
      name: pointsToRank(profileGameStatistics.general.points) || 'unknown',
      info: profileGameStatistics.general.points || 0,
    },
  };
}

export function getRankStats(gameData, generalData) {
  const stats = [];
  RANKS_TO_SHOW.forEach(rankName => {
    if (!gameData.rank[rankName]) {
      return;
    }

    if (rankName === 'hub') {
      if (!generalData.hubMembership[0]) {
        return;
      }
      const selectedHubData = generalData.hubMembership[0];
      // Note; When hub selector feature ready in backend.
      // const selectedHubData = generalData.hubMembership.find(hub => hub.id === gameData.rank.selectedHub);
      if (!selectedHubData) {
        return;
      }

      stats.push({ rank: `#${gameData.rank[rankName]} rank`, location: selectedHubData.name, icon: rankName });
      return;
    }

    stats.push({ rank: `#${gameData.rank[rankName]} rank`, location: generalData[rankName], icon: rankName });
  });

  return {
    playerName: gameData.general.playerName,
    rankAvatar: 'boomtv/' + pointsToRank(gameData.general.points), // Using shared BoomTV Ranks
    rank: {
      name: pointsToRank(gameData.general.points),
      info: gameData.general.points,
    },
    stats,
  };
}

export function getOverviewStats(gameData) {
  // fortnite does not have winRatio on stats server
  // use wins / matches to get ratio
  return {
    wins: gameData.general.wins,
    winRatio: gameData.general.matches !== 0 ? Math.round(gameData.general.wins / gameData.general.matches * 100) : 0,
  };
}

export function getQueuesStats(queuesMap) {
  const queues = [];
  for (const [queueName, queueStats] of Object.entries(queuesMap)) {
    const stats = [];

    FORTNITE_QUEUES_STATS_ORDER.forEach(statName => {
      stats.push({
        name: FORTNITE_STATS_NAME[statName],
        nameAbbr: FORTNITE_STATS_NAME_ABBR[statName] || FORTNITE_STATS_NAME[statName],
        value: queueStats[statName],
        symbol: ProfileHelpers.parseStatSymbol(statName, FORTNITE_STATS_TYPES),
        inverted: FORTNITE_STATS_INVERTED[statName],
      });
    });

    queues.push({
      ...queueStats,
      icon: queueName,
      type: queueName,
      stats,
    });
  }

  return queues;
}

export function getMonthlyStats(gameData) {
  let monthAvgPerformance = 0;
  let overallPerformance = gameData.general.avgPerformance;
  overallPerformance = overallPerformance > 0 ? overallPerformance : 1;
  const dailyActivity = gameData.activity.dailyActivity;
  dailyActivity.forEach((day, i) => {
    monthAvgPerformance += day.avgPerformance;

    const stats = FORTNITE_MONTHLY_STATS_TOOLTIP_ORDER.map(statName => {
      return {
        name: FORTNITE_STATS_NAME[statName],
        value: day[statName],
        symbol: ProfileHelpers.parseStatSymbol(statName, FORTNITE_STATS_TYPES),
      };
    });

    // Note: We add performancePercentage to day data.
    dailyActivity[i] = {
      ...day,
      stats,
      performancePercentage: day.matches ? ((day.avgPerformance * 100) / overallPerformance).toFixed() : 0,
    };
  });

  // Note: We add the performancePercentage to month data.
  monthAvgPerformance = (monthAvgPerformance / gameData.activity.dailyActivity.length).toFixed();
  monthAvgPerformance = ((monthAvgPerformance * 100) / overallPerformance).toFixed();

  return {
    days: gameData.activity.dailyActivity,
    week: gameData.activity.weekActivity,
    month: {
      ...gameData.activity.monthActivity,
      performancePercentage: monthAvgPerformance,
    },
  };
}

export function getRankProgression(gameData) {
  const points = [];
  const times = [];
  gameData.rankProgression.forEach(rankData => {
    points.push(rankData.score);
    // NOTE with .utc() it will take +1 day in negative Z zones (like argentina -3)
    times.push(dayjs.unix(rankData.date).toISOString());
  });

  return { points, times };
}

export function getCoreStats(gameData) {
  const stats = [];
  let maxGraphValue = 0;

  FORTNITE_CORE_STATS_ORDER.forEach(statName => {
    stats.push({
      name: FORTNITE_STATS_NAME[statName],
      playerValue: gameData.general[statName],
      othersValue: gameData.general.others[statName],
      symbol: ProfileHelpers.parseStatSymbol(statName, FORTNITE_STATS_TYPES),
    });

    maxGraphValue = gameData.general[statName] > maxGraphValue ? gameData.general[statName] : maxGraphValue;
    maxGraphValue = gameData.general.others[statName] > maxGraphValue ? gameData.general.others[statName] : maxGraphValue;
  });

  return { maxGraphValue, stats };
}
export function getCoreStatsDiff(current, opponent) {
  const currentAvgKills = current.stats.find(stat => stat.name === FORTNITE_STATS_NAME.avgKills).playerValue;
  const opponentAvgKills = opponent.stats.find(stat => stat.name === FORTNITE_STATS_NAME.avgKills).playerValue;
  const currentAvgPlacement = current.stats.find(stat => stat.name === FORTNITE_STATS_NAME.avgPlacement).playerValue;
  const opponentAvgPlacement = opponent.stats.find(stat => stat.name === FORTNITE_STATS_NAME.avgPlacement).playerValue;

  // If current and opponent avgKills is 0, we assign diff to 0%.
  if (!currentAvgKills && !opponentAvgKills) {
    return 0;
  }

  const diffAvgKills = ProfileHelpers.calculateDiffPercent(currentAvgKills, opponentAvgKills);
  // If current OR opponent avgKills is 0, we assign the player with kills diff to 100%.
  if (diffAvgKills === 100 || diffAvgKills === -100) {
    return diffAvgKills.toFixed();
  }

  // Christian Equation (rounded to 2 decimal places)
  return (((((opponentAvgKills / currentAvgKills) - 1) + ((1 / opponentAvgPlacement) / (1 / currentAvgPlacement)) - 1)) / 2).toFixed(2);
}

export function getleaderboardListWithStatName(leaderboardList) {
  return leaderboardList.map(player => {
    return { ...player, statName: FORTNITE_STATS_NAME[player.leaderboardType].toLowerCase() };
  });
}
